'use client';

import { useMutateRequestTeamPlanUpgrade } from '@/hooks/mutation/useMutateRequestTeamPlanUpgrade';
import { trackTeamTrialUpgradeRequestViewed } from '@/models/analytics/features/trackTeamTrialUpgradeRequestAnalytics';
import { type ErrorResponse } from '@/types';
import { logger } from '@/utils/logger/logger';
import {
  TEAM_TRIAL_UPGRADE_REQUEST_ACTION,
  TRIALS_AND_UPGRADES_SCREEN,
} from '@heidi/commerce/contracts';
import { useUserTier } from '@heidi/commerce/data';
import type { ButtonVariant } from '@heidi/design-system/button';
import type { FramedIconState } from '@heidi/design-system/framed-icon';
import { toast } from '@heidi/design-system/toast';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
  createTeamTrialUpgradeRequestModalSchema,
  teamTrialUpgradeRequestModalMessages,
  type TeamTrialUpgradeRequestModalFormValues,
} from '@heidi/commerce';
import {
  closeTeamTrialUpgradeRequestModalAtom,
  teamTrialUpgradeRequestModalAtom,
} from './atoms';

const noop = () => {};

export function useTeamTrialUpgradeRequestModal() {
  const state = useAtomValue(teamTrialUpgradeRequestModalAtom);
  const setClosed = useSetAtom(closeTeamTrialUpgradeRequestModalAtom);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setClosed();
      }
    },
    [setClosed]
  );

  const open = state.isOpen;
  const plan = state.isOpen ? state.plan : SubscriptionTier.CLINICIAN_PRO;
  // The submit emit belongs to whoever opened the modal, so while it is closed
  // there is no origin and nothing to report. This used to default to the recap
  // modal's `ScreenName` — a placeholder that could only ever be wrong, since a
  // closed modal has no submit to attribute.
  const trackRequestSubmitted = state.isOpen ? state.trackRequestSubmitted : noop;
  const onRequestSent = state.isOpen ? (state.onRequestSent ?? noop) : noop;
  const onUserDismiss = state.isOpen ? state.onUserDismiss : undefined;

  const intl = useIntl();
  const { userTier } = useUserTier();
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const teamId = useMemo(() => teamDetails?.team?.id, [teamDetails]);
  const {
    mutateAsync: mutateRequestTeamPlanUpgrade,
    isPending: isRequestingTeamPlanUpgrade,
  } = useMutateRequestTeamPlanUpgrade();
  const closingForSuccessRef = useRef(false);

  const messagePlaceholder = useMemo(
    () =>
      intl.formatMessage(
        plan === SubscriptionTier.CLINICIAN_PRO_PLUS
          ? teamTrialUpgradeRequestModalMessages.messagePlaceholderPractice
          : teamTrialUpgradeRequestModalMessages.messagePlaceholderClinician
      ),
    [intl, plan]
  );

  const teamTrialUpgradeRequestModalSchema = useMemo(
    () => createTeamTrialUpgradeRequestModalSchema(intl.formatMessage),
    [intl.formatMessage]
  );
  const resolver = useMemo(
    () => zodResolver(teamTrialUpgradeRequestModalSchema),
    [teamTrialUpgradeRequestModalSchema]
  );

  const form = useForm<TeamTrialUpgradeRequestModalFormValues>({
    resolver,
    mode: 'onChange',
    defaultValues: {
      message: '',
    },
  });
  const { reset } = form;

  useEffect(() => {
    if (!open) return;
    reset({ message: '' });
  }, [open, plan, reset]);

  useEffect(() => {
    if (!open) return;
    trackTeamTrialUpgradeRequestViewed({
      screen_name: TRIALS_AND_UPGRADES_SCREEN.teamTrialUpgradeRequest,
      ...(userTier !== undefined && { current_plan: userTier }),
    });
  }, [open, userTier]);

  const tierState: Extract<FramedIconState, 'pro' | 'practice'> =
    plan === SubscriptionTier.CLINICIAN_PRO_PLUS ? 'practice' : 'pro';
  // "Send" is a generic action (sending a message to the admin), not a tier
  // upgrade, so the button uses the standard primary variant for both plans.
  // Figma: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10993-36652
  const sendVariant: ButtonVariant = 'default';

  const titleMessage =
    plan === SubscriptionTier.CLINICIAN_PRO_PLUS
      ? teamTrialUpgradeRequestModalMessages.titlePractice
      : teamTrialUpgradeRequestModalMessages.titleClinician;
  const descriptionMessage =
    plan === SubscriptionTier.CLINICIAN_PRO_PLUS
      ? teamTrialUpgradeRequestModalMessages.descriptionPractice
      : teamTrialUpgradeRequestModalMessages.descriptionClinician;

  const defaultErrorTitle = useMemo(
    () => intl.formatMessage(teamTrialUpgradeRequestModalMessages.requestFailed),
    [intl]
  );

  const handleDialogOpenChange = useCallback(
    (next: boolean) => {
      if (!next && isRequestingTeamPlanUpgrade) {
        return;
      }
      if (!next && open) {
        if (!closingForSuccessRef.current) {
          onUserDismiss?.();
        }
        closingForSuccessRef.current = false;
      }
      handleOpenChange(next);
    },
    [handleOpenChange, isRequestingTeamPlanUpgrade, onUserDismiss, open]
  );

  const onSubmit = useCallback(
    async (data: TeamTrialUpgradeRequestModalFormValues) => {
      if (!teamId) {
        logger.error('Failed to request team plan upgrade: missing team id');
        toast({
          title: defaultErrorTitle,
          tone: 'destructive',
        });
        return;
      }

      trackRequestSubmitted({
        action: TEAM_TRIAL_UPGRADE_REQUEST_ACTION.requestAdminUpgrade,
        label: intl.formatMessage(teamTrialUpgradeRequestModalMessages.send),
        ...(userTier !== undefined && { current_plan: userTier }),
        is_team_member: true,
      });

      const trimmed = data.message.trim();

      try {
        await mutateRequestTeamPlanUpgrade({
          teamId,
          body: {
            target_plan: plan,
            ...(trimmed.length > 0 ? { message: trimmed } : {}),
          },
        });
        closingForSuccessRef.current = true;
        onRequestSent();
        toast({
          title: intl.formatMessage(
            teamTrialUpgradeRequestModalMessages.requestSentSuccess
          ),
          tone: 'success',
        });
        handleDialogOpenChange(false);
      } catch (error) {
        let description: string | undefined;
        if (error instanceof AxiosError) {
          const errorData = error.response?.data as ErrorResponse | undefined;
          description = errorData?.detail?.msg;
        }
        toast({
          title: defaultErrorTitle,
          ...(description ? { description } : {}),
          tone: 'destructive',
        });
      }
    },
    [
      defaultErrorTitle,
      handleDialogOpenChange,
      intl,
      mutateRequestTeamPlanUpgrade,
      onRequestSent,
      trackRequestSubmitted,
      plan,
      teamId,
      userTier,
    ]
  );

  const handleBackOrCancel = useCallback(() => {
    handleDialogOpenChange(false);
  }, [handleDialogOpenChange]);

  const backAriaLabel = useMemo(
    () => intl.formatMessage(teamTrialUpgradeRequestModalMessages.back),
    [intl]
  );

  return {
    open,
    form,
    messagePlaceholder,
    tierState,
    sendVariant,
    titleMessage,
    descriptionMessage,
    handleDialogOpenChange,
    handleBackOrCancel,
    onSubmit,
    isRequestingTeamPlanUpgrade,
    backAriaLabel,
  };
}
